import React, { forwardRef } from 'react';
import classNames from 'classnames';

import { Comp, useId } from '@redsift/design-system';

import { ChartSize, ColorTheme, TooltipVariant } from '../../types';
import { LineChartLegendVariant, LineChartProps } from './types';
import { LoadingLineChart } from './LoadingLineChart';
import { EmptyLineChart } from './EmptyLineChart';
import { RenderedLineChart } from './RenderedLineChart';
import { AxisVariant } from '../Axis';

const COMPONENT_NAME = 'LineChart';
const CLASSNAME = 'redsift-line-chart';

/**
 * LineChart displays trends over time or continuous data.
 * Built on D3.js with smooth interpolation and interactive data points.
 *
 * This is the only public entry point for line charts. Do not import
 * `RenderedLineChart`, `EmptyLineChart`, or `LoadingLineChart` directly —
 * those are internal sub-components that `LineChart` selects automatically
 * based on whether `data` is present and non-empty.
 *
 * Data format: `LinearData` `[{ key: Date, value: number, label?: string }]`
 *
 * @example
 * // Basic line chart with time data
 * const data = [
 *   { key: new Date('2024-01'), value: 100, label: 'January' },
 *   { key: new Date('2024-02'), value: 150, label: 'February' },
 *   { key: new Date('2024-03'), value: 125, label: 'March' },
 * ];
 * <LineChart data={data} size="medium" />
 *
 * @example
 * // With tooltips and click handler
 * <LineChart
 *   data={timeSeriesData}
 *   tooltipVariant="value"
 *   onDotClick={(d) => showDetails(d)}
 * />
 *
 * @example
 * // Multiple lines with legend (TwoCategoryData format)
 * const multiSeriesData = [
 *   { key: [new Date('2024-01'), 'Series A'], value: 100 },
 *   { key: [new Date('2024-01'), 'Series B'], value: 80 },
 *   { key: [new Date('2024-02'), 'Series A'], value: 120 },
 *   { key: [new Date('2024-02'), 'Series B'], value: 95 },
 * ];
 * <LineChart
 *   data={multiSeriesData}
 *   legendVariant="externalLabel"
 *   colorTheme="dark"
 * />
 *
 * @example
 * // Customize axis placement and variant
 * <LineChart
 *   data={data}
 *   xAxisPlacement="top"
 *   yAxisPlacement="right"
 *   xAxisVariant="minimal"
 * />
 */

export const LineChart: Comp<LineChartProps, HTMLDivElement> = forwardRef((props, ref) => {
  const {
    className,
    colorTheme = ColorTheme.dark,
    data: propsData,
    dotRole,
    emptyComponent,
    id: propsId,
    isDotSelected,
    labelDecorator,
    legendDecorator,
    legendVariant: propsLegendVariant,
    localeText = { emptyChartText: 'No Data' },
    onDotClick,
    size = ChartSize.medium,
    sortingMethod = 'desc-value',
    tooltipDecorator,
    tooltipVariant = TooltipVariant.none,
    xAxisPlacement = 'bottom',
    xAxisVariant = AxisVariant.default,
    yAxisPlacement = 'left',
    yAxisVariant = AxisVariant.default,
    ...forwardedProps
  } = props;
  const [_id] = useId();
  const id = propsId ?? _id;

  const legendVariant = propsLegendVariant ?? LineChartLegendVariant.externalLabel;

  if (propsData === undefined || propsData === null) {
    return <LoadingLineChart id={id} {...forwardedProps} ref={ref} />;
  }

  if (propsData.length === 0) {
    return (
      <EmptyLineChart
        data={propsData}
        emptyComponent={emptyComponent}
        localeText={localeText}
        size={size}
        sortingMethod={sortingMethod}
        xAxisPlacement={xAxisPlacement}
        xAxisVariant={xAxisVariant}
        yAxisPlacement={yAxisPlacement}
        yAxisVariant={yAxisVariant}
        {...forwardedProps}
        ref={ref}
      />
    );
  }

  return (
    <RenderedLineChart
      className={classNames(LineChart.className, className)}
      colorTheme={colorTheme}
      data={propsData}
      dotRole={dotRole}
      id={id}
      isDotSelected={isDotSelected}
      labelDecorator={labelDecorator}
      legendDecorator={legendDecorator}
      legendVariant={legendVariant}
      onDotClick={onDotClick}
      size={size}
      sortingMethod={sortingMethod}
      tooltipDecorator={tooltipDecorator}
      tooltipVariant={tooltipVariant}
      xAxisPlacement={xAxisPlacement}
      xAxisVariant={xAxisVariant}
      yAxisPlacement={yAxisPlacement}
      yAxisVariant={yAxisVariant}
      {...forwardedProps}
      ref={ref}
    />
  );
});
LineChart.className = CLASSNAME;
LineChart.displayName = COMPONENT_NAME;
